import React, { useMemo, useState } from 'react';
import { Card, Checkbox, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { AOI_DETAIL_COLORS, LayerVisibility, aoiSourceLabel } from '../constants';
import { BizAoiAttributeDetail } from '../graphql/queries/getAoiAttributeDetail';
import { parseDeliveryTime } from '../utils/deliveryTime';
import DeliveryTimeView from './DeliveryTimeView';

interface Props {
    detail: BizAoiAttributeDetail;
    layerVisibility: LayerVisibility;
    onLayerVisibilityChange: (next: LayerVisibility) => void;
    /** 地址级点位"按地址查看"当前选中的 address_core_id; null=看全部 */
    selectedAddressId?: number | null;
    /** 选中/取消某地址(传 null 取消); 由父组件同步给地图做高亮 */
    onSelectAddress?: (addressCoreId: number | null) => void;
}

/**
 * 详情页右侧信息面板 - 一组卡片堆叠 (垂直滚动)。
 *
 * <p>各 has 类目的卡片头部有显示/隐藏 Checkbox, 控制对应图层在地图上的可见性。
 * 需求里说 "前方有勾选框,控制是否在地图上展示", 这里实现成卡片标题旁的开关。
 */
const AoiDetailInfoPanel: React.FC<Props> = ({
    detail, layerVisibility, onLayerVisibilityChange, selectedAddressId = null, onSelectAddress,
}) => {
    const { t } = useTranslation();
    const attribute = detail.attribute;
    const isBuilding = detail.level === 0;
    const isCommunity = detail.level === 1;

    const updateVisibility = (patch: Partial<LayerVisibility>) =>
        onLayerVisibilityChange({ ...layerVisibility, ...patch });

    return (
        <div className="space-y-3 overflow-y-auto h-full pr-1">
            {/* 基础信息 */}
            <Card size="small" title="基础信息">
                {/*
                 * CSS Grid 两列 - 旧版用 flex justify-between, 短 label + 短 value 中间撑出巨大空白.
                 * max-content 让 label 列自动收缩到该 Card 内最长 label 的宽度 (e.g. "更新时间"),
                 * label 文字 text-right 让短 label 也右对齐到列右缘 (向 value 靠拢);
                 * value 列 1fr 占剩余, 内容默认左对齐, 跟 label 之间只剩 gap-x-3 一个间距.
                 */}
                <div
                    className="grid gap-x-3 gap-y-2.5 items-center text-sm leading-relaxed"
                    style={{ gridTemplateColumns: 'max-content 1fr' }}
                >
                    <InfoRow label="AOI ID" value={String(detail.aoiId)} />
                    <InfoRow label="层级" value={
                        detail.level === 0
                            ? <Tag color="blue">{t('mapTool.aoiPlatform.options.levelBuilding')}</Tag>
                            : detail.level === 1
                                ? <Tag color="purple">{t('mapTool.aoiPlatform.options.levelCommunity')}</Tag>
                                : '-'
                    } />
                    <InfoRow label="名称" value={detail.displayName ?? '-'} />
                    <InfoRow label="大号" value={detail.houseNumber ?? '-'} />
                    <InfoRow label="来源" value={
                        detail.dataSource == null
                            ? '-'
                            : detail.dataSource === 'SURVEY'
                                ? <Tag color="green">{aoiSourceLabel(detail.dataSource)}</Tag>
                                : <Tag>{aoiSourceLabel(detail.dataSource)}</Tag>
                    } />
                    <InfoRow label="中心点" value={
                        detail.centerLatitude != null && detail.centerLongitude != null
                            ? `${detail.centerLatitude.toFixed(7)}, ${detail.centerLongitude.toFixed(7)}`
                            : '-'
                    } />
                    <InfoRow label="邮编" value={detail.zipcode ?? '-'} />
                    <InfoRow label="类型" value={attribute?.typeZhPath ?? '-'} />
                    <InfoRow label="状态" value={
                        detail.status === 1
                            ? <Tag color="green">Active</Tag>
                            : detail.status === 0
                                ? <Tag color="red">Inactive</Tag>
                                : '-'
                    } />
                    <InfoRow label="更新时间" value={
                        detail.aoiUpdatedAt ? detail.aoiUpdatedAt.replace('T', ' ').slice(0, 19) : '-'
                    } />
                </div>
            </Card>

            {/* 进门点 */}
            <Card
                size="small"
                title={
                    <SectionTitle
                        text="进门点"
                        color={AOI_DETAIL_COLORS.entryPoint}
                        count={attribute?.entryPoints.length ?? 0}
                        visible={layerVisibility.showEntryPoints}
                        onToggle={(v) => updateVisibility({ showEntryPoints: v })}
                    />
                }
            >
                {attribute?.entryPoints.length ? (
                    <PointList points={attribute.entryPoints} />
                ) : <span className="text-xs text-gray-400">无</span>}
            </Card>

            {/* 停车点 */}
            <Card
                size="small"
                title={
                    <SectionTitle
                        text="停车点"
                        color={AOI_DETAIL_COLORS.parkingPoint}
                        count={attribute?.parkingPoints.length ?? 0}
                        visible={layerVisibility.showParkingPoints}
                        onToggle={(v) => updateVisibility({ showParkingPoints: v })}
                    />
                }
            >
                {attribute?.parkingPoints.length ? (
                    <PointList points={attribute.parkingPoints} />
                ) : <span className="text-xs text-gray-400">无</span>}
            </Card>

            {/* 配送时间 - 结构化展示 */}
            <DeliveryTimeCard rawJson={attribute?.deliveryTime ?? null} />

            {/* 门禁 */}
            <Card size="small" title="门禁 code">
                {attribute?.accessCodes.length ? (
                    /*
                     * 4 列严格 1fr 等分 - repeat(4, 1fr) 把 row 宽度切成 4 等份, 每个字段一列.
                     * minmax(0, 1fr) 允许 cell 在内容超出时不撑大列 (用 truncate 处理), 保证等宽不漂移.
                     *
                     *   类型 tag (col1) | code 值 (col2) | 状态 tag (col3) | 备注 (col4)
                     *
                     * 列内对齐: tag 默认 justify-self:start (左贴齐自己 cell 左缘),
                     * code 值默认左对齐, 备注 truncate; 整行 4 个字段视觉上均匀铺满, 不再扎堆左侧.
                     */
                    <div
                        className="grid gap-x-3 gap-y-1.5 items-center text-sm"
                        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
                    >
                        {attribute.accessCodes.map((c, idx) => (
                            <React.Fragment key={idx}>
                                {/* justify-self:start 让 tag 在 1fr cell 内左对齐 (不被 cell 撑成全宽) */}
                                <Tag style={{ margin: 0, justifySelf: 'start' }}>
                                    {ACCESS_CODE_TYPE_LABELS[c.codeType ?? ''] ?? c.codeType ?? '-'}
                                </Tag>
                                <span className="truncate">{c.codeValue ?? '-'}</span>
                                <Tag
                                    color={ACCESS_CODE_STATUS_COLORS[c.available ?? ''] ?? 'default'}
                                    style={{ margin: 0, justifySelf: 'start' }}
                                >
                                    {ACCESS_CODE_STATUS_LABELS[c.available ?? ''] ?? c.available ?? '-'}
                                </Tag>
                                <span className="text-gray-500 text-xs truncate">{c.remark ?? ''}</span>
                            </React.Fragment>
                        ))}
                    </div>
                ) : <span className="text-xs text-gray-400">无</span>}
            </Card>

            {/* 地址级点位 (关联地址各自的进门/停车点, 与楼级是两层数据; 地图上空心圈样式) */}
            {isBuilding && (
                <Card
                    size="small"
                    title={
                        <SectionTitle
                            text="地址级点位"
                            color={AOI_DETAIL_COLORS.entryPoint}
                            hollow
                            count={addressPointCount(detail)}
                            visible={layerVisibility.showAddressPoints}
                            onToggle={(v) => updateVisibility({ showAddressPoints: v })}
                        />
                    }
                >
                    <AddressPointSwitcher
                        addresses={detail.relatedAddresses}
                        selectedAddressId={selectedAddressId}
                        onSelectAddress={onSelectAddress}
                    />
                </Card>
            )}

            {/* 关联地址 (1级 AOI) */}
            {isBuilding && (
                <Card
                    size="small"
                    title={
                        <SectionTitle
                            text="关联地址"
                            color={AOI_DETAIL_COLORS.relatedAddress}
                            count={detail.relatedAddresses.length}
                            visible={layerVisibility.showRelatedAddresses}
                            onToggle={(v) => updateVisibility({ showRelatedAddresses: v })}
                        />
                    }
                >
                    {detail.relatedAddresses.length ? (
                        <ExpandableAddressList addresses={detail.relatedAddresses} />
                    ) : <span className="text-xs text-gray-400">无</span>}
                </Card>
            )}

            {/* 子 AOI (2级 AOI) */}
            {isCommunity && (
                <Card
                    size="small"
                    title={
                        <SectionTitle
                            text="子 AOI"
                            color={AOI_DETAIL_COLORS.child}
                            count={detail.childAois.length}
                            visible={layerVisibility.showChildren}
                            onToggle={(v) => updateVisibility({ showChildren: v })}
                        />
                    }
                >
                    {detail.childAois.length ? (
                        <div className="space-y-1">
                            {detail.childAois.map((c) => (
                                <div key={c.aoiId} className="text-sm">
                                    <Tag color="cyan">{c.aoiId}</Tag>
                                    <span>{c.displayName ?? '-'}</span>
                                </div>
                            ))}
                        </div>
                    ) : <span className="text-xs text-gray-400">无</span>}
                </Card>
            )}

            {/* 周边 AOI 图层开关 */}
            <Card size="small" title={
                <SectionTitle
                    text="周边 AOI"
                    color={AOI_DETAIL_COLORS.nearby}
                    count={detail.nearbyGeometries.length}
                    visible={layerVisibility.showNearby}
                    onToggle={(v) => updateVisibility({ showNearby: v })}
                />
            }>
                <div className="text-xs text-gray-500">显示当前 AOI 周边 500m 内的 AOI 多边形, 仅展示, 不可点击。</div>
            </Card>
        </div>
    );
};

/** 关联地址列表默认折叠条数 — 超过这个数显示「还有 X 个」按钮, 点击展开全部。 */
const ADDRESS_PREVIEW_LIMIT = 5;

/** 地址级点位总数 (进门 + 停车, 跨全部关联地址)。 */
const addressPointCount = (detail: BizAoiAttributeDetail): number =>
    detail.relatedAddresses.reduce(
        (sum, a) => sum + (a.entryPoints?.length ?? 0) + (a.parkingPoints?.length ?? 0), 0);

/** 关联地址 fullStandardAddress → 短标签(取 "Apt/Unit/#..." 单元段, 没有则截尾)。 */
const shortAddressLabel = (full: string | null, addressCoreId: number): string => {
    if (!full) return `#${addressCoreId}`;
    const m = full.match(/\b(Apt|Unit|Ste|Suite|#)\s*\.?\s*([\w-]+)/i);
    if (m) return `${m[1]} ${m[2]}`;
    // 无单元号: 取首段(门牌+街) 前 16 字符
    const head = full.split(',')[0];
    return head.length > 16 ? head.slice(0, 16) + '…' : head;
};

/**
 * 地址级点位"按地址查看"切换器: 列出有点位的关联地址为可点 chip, 点一个 → 地图高亮该地址
 * 自己的进门/停车点(其余淡化), 再点或点"全部"取消。选中后展开该地址的点位坐标明细。
 * 解决"图上多个进门点分不清哪个属于哪个地址"的问题。
 */
const AddressPointSwitcher: React.FC<{
    addresses: BizAoiAttributeDetail['relatedAddresses'];
    selectedAddressId: number | null;
    onSelectAddress?: (addressCoreId: number | null) => void;
}> = ({ addresses, selectedAddressId, onSelectAddress }) => {
    const withPoints = addresses.filter(
        (a) => (a.entryPoints?.length ?? 0) + (a.parkingPoints?.length ?? 0) > 0);
    if (!withPoints.length) {
        return <span className="text-xs text-gray-400">无地址级点位</span>;
    }
    const selected = withPoints.find((a) => a.addressCoreId === selectedAddressId) ?? null;
    const chipBase: React.CSSProperties = {
        cursor: 'pointer', margin: 0, userSelect: 'none',
    };
    return (
        <div className="space-y-2">
            <div className="text-xs text-gray-500">
                点地址查看它自己的进门/停车点(地图高亮), 再点或点"全部"取消。
            </div>
            <div className="flex flex-wrap gap-1.5">
                <Tag
                    color={selectedAddressId == null ? 'green' : 'default'}
                    style={chipBase}
                    onClick={() => onSelectAddress?.(null)}
                >全部</Tag>
                {withPoints.map((a) => {
                    const n = (a.entryPoints?.length ?? 0) + (a.parkingPoints?.length ?? 0);
                    const on = a.addressCoreId === selectedAddressId;
                    return (
                        <Tag
                            key={a.addressCoreId}
                            color={on ? 'green' : 'default'}
                            style={chipBase}
                            onClick={() => onSelectAddress?.(on ? null : a.addressCoreId)}
                        >{shortAddressLabel(a.fullStandardAddress, a.addressCoreId)} · {n}</Tag>
                    );
                })}
            </div>
            {selected && (
                <div className="text-sm bg-white rounded border border-gray-200 p-2 space-y-1">
                    <div className="font-medium text-gray-800">{selected.fullStandardAddress ?? `#${selected.addressCoreId}`}</div>
                    <SelectedPointRows label="进门点" color={AOI_DETAIL_COLORS.entryPoint} points={selected.entryPoints} />
                    <SelectedPointRows label="停车点" color={AOI_DETAIL_COLORS.parkingPoint} points={selected.parkingPoints} />
                </div>
            )}
        </div>
    );
};

const SelectedPointRows: React.FC<{
    label: string;
    color: string;
    points?: { lng: number | null; lat: number | null; remark: string | null }[];
}> = ({ label, color, points }) => {
    if (!points || !points.length) return null;
    return (
        <div className="space-y-0.5">
            {points.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="inline-block w-2.5 h-2.5 rounded-full box-border"
                        style={{ background: '#fff', border: `2px solid ${color}` }} />
                    <span className="text-gray-500">{label}</span>
                    <span className="text-gray-700 font-mono">
                        {p.lat != null && p.lng != null ? `${p.lat.toFixed(7)}, ${p.lng.toFixed(7)}` : '-'}
                    </span>
                    {p.remark && <span className="text-gray-400">（{p.remark}）</span>}
                </div>
            ))}
        </div>
    );
};

/**
 * 门禁 code 字段 → 中文展示映射 (跟 AccessCodeEditor 的下拉选项保持一致)。
 * 表里存的是 enum code (Gate / MainDoor / ...), 展示时映射成中文; 没匹配到的 fallback 显示原 code。
 */
const ACCESS_CODE_TYPE_LABELS: Record<string, string> = {
    Gate: '社区门禁',
    MainDoor: '楼门门禁',
    SideDoor: '侧门门禁',
    Elevate: '电梯门禁',
    Garage: '车库门禁',
    ParcelStop: '快递柜',
};

/** 门禁状态 (DB 存 enum.name) → 中文 + Tag 颜色映射。 */
const ACCESS_CODE_STATUS_LABELS: Record<string, string> = {
    PENDING_VERIFICATION: '待验证',
    AVAILABLE: '可用',
    UNAVAILABLE: '不可用',
};
const ACCESS_CODE_STATUS_COLORS: Record<string, string> = {
    PENDING_VERIFICATION: 'orange',
    AVAILABLE: 'green',
    UNAVAILABLE: 'red',
};

/**
 * 关联地址列表 - 默认只展示前 {@link ADDRESS_PREVIEW_LIMIT} 条, 多了显示可点击的
 * 「还有 X 个地址 (点击展开)」按钮; 展开后变成「收起」按钮把列表收回去。
 *
 * <p>样式: 按钮用 primary 蓝 + 可见的字号, 让用户一眼知道这是可点击的, 避免被当成只读说明文字。
 */
const ExpandableAddressList: React.FC<{
    addresses: { addressCoreId: number; fullStandardAddress: string | null; latitude: number | null; longitude: number | null }[];
}> = ({ addresses }) => {
    const [expanded, setExpanded] = useState(false);
    const overflow = addresses.length - ADDRESS_PREVIEW_LIMIT;
    const visible = expanded ? addresses : addresses.slice(0, ADDRESS_PREVIEW_LIMIT);

    return (
        <div className="space-y-1">
            {visible.map((a) => (
                <div key={a.addressCoreId} className="text-sm">
                    <div>{a.fullStandardAddress ?? '-'}</div>
                    <div className="text-xs text-gray-500">
                        {a.latitude != null && a.longitude != null
                            ? `${a.latitude.toFixed(7)}, ${a.longitude.toFixed(7)}`
                            : '-'}
                    </div>
                </div>
            ))}
            {overflow > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline cursor-pointer mt-1"
                >
                    {expanded ? '收起' : `还有 ${overflow} 个地址 (点击展开)`}
                </button>
            )}
        </div>
    );
};

/**
 * 配送时间卡片 - 拿后端 JSON 字符串 parse 成结构化对象后用 {@link DeliveryTimeView} 展示。
 * parse 跟 panel 渲染解耦, 避免 panel 每次 render 都 parse。
 */
const DeliveryTimeCard: React.FC<{ rawJson: string | null }> = ({ rawJson }) => {
    const parsed = useMemo(() => parseDeliveryTime(rawJson), [rawJson]);
    return (
        <Card size="small" title="配送时间">
            <DeliveryTimeView value={parsed} />
        </Card>
    );
};

/**
 * InfoRow 是 grid fragment - 输出两个 cell, 实际列宽 / 间距由父级 grid 容器统一控制.
 * label 左对齐 (col1 = max-content, label 占满 col1, 自然左对齐到 card 左缘).
 */
const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <>
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-900 break-words">{value}</span>
    </>
);

const SectionTitle: React.FC<{
    text: string;
    color: string;
    count: number;
    visible: boolean;
    onToggle: (v: boolean) => void;
    /** true = 空心圈样式 (呼应地图上地址级点位的空心 marker) */
    hollow?: boolean;
}> = ({ text, color, count, visible, onToggle, hollow }) => (
    <div className="flex items-center gap-2">
        {/* 勾选框放在最前面, 控制对应图层在地图上的可见性 */}
        <Checkbox checked={visible} onChange={(e) => onToggle(e.target.checked)} />
        <span
            className="inline-block w-3 h-3 rounded-full box-border"
            style={hollow ? { background: '#fff', border: `2.5px solid ${color}` } : { background: color }}
        />
        <span>{text}</span>
        <Tag>{count}</Tag>
    </div>
);

const PointList: React.FC<{ points: { lng: number | null; lat: number | null; remark: string | null }[] }> = ({ points }) => (
    // 跟基础信息 Card 同款 grid 两列布局, label (remark/序号) 左对齐, 坐标左对齐
    <div
        className="grid gap-x-3 gap-y-1.5 items-baseline text-sm"
        style={{ gridTemplateColumns: 'max-content 1fr' }}
    >
        {points.map((p, idx) => (
            <React.Fragment key={idx}>
                <span className="text-gray-500">{p.remark ?? `#${idx + 1}`}</span>
                <span className="text-gray-900">
                    {p.lat != null && p.lng != null ? `${p.lat.toFixed(7)}, ${p.lng.toFixed(7)}` : '-'}
                </span>
            </React.Fragment>
        ))}
    </div>
);

export default AoiDetailInfoPanel;
